import { Component, Event, EventEmitter, h, Prop, Element } from '@stencil/core';

// Static counter for unique textarea names
const nameCounters: Record<string, number> = {};

/**
 * Multi-line text input form control with floating label, character counter, and validation states.
 * Supports placeholder, disabled, required, and maxlength constraints. Emits input and change events.
 *
 * @example
 *   <me-textarea label="Message" name="message" placeholder="Type your message..." required></me-textarea>
 */
@Component({
  tag: 'me-textarea',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Textarea {
  @Element() hostEl!: HTMLElement;

  /**
   * Placeholder text when no value is entered
   * @default ''
   */
  @Prop() placeholder: string = '';

  /**
   * The value of the textarea
   * @default ''
   */
  @Prop({ mutable: true }) value: string = '';

  /**
   * The name of the textarea when used in a form
   * @default ''
   */
  @Prop({ reflect: true }) name: string = '';

  /**
   * Whether the textarea is disabled
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * Whether the textarea is required in a form
   * @default false
   */
  @Prop() required: boolean = false;

  /**
   * The number of rows to show
   * @default 3
   */
  @Prop() rows: number = 3;

  /**
   * Emitted when the textarea value changes
   * @event valueChange
   */
  @Event() valueChange: EventEmitter<string>;

  /**
   * Emitted on each input
   * @event valueInput
   */
  @Event() valueInput: EventEmitter<string>;

  @Prop() label?: string;

  /**
   * Error state for textarea (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) error?: string;

  /**
   * Error message for textarea (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorType?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Minimum length for textarea value
   */
  @Prop() minLength?: number;

  /**
   * Maximum length for textarea value
   */
  @Prop() maxLength?: number;

  /**
   * Custom message for minLength error
   */
  @Prop() minLengthMessage?: string;

  /**
   * Custom message for maxLength error
   */
  @Prop() maxLengthMessage?: string;

  @Prop() formId?: string;

  @Event() clearFieldError: EventEmitter<string>;

  componentWillLoad() {
    const baseName = this.name || 'textarea';
    if (!nameCounters[baseName]) {
      nameCounters[baseName] = 0;
    }
    nameCounters[baseName]++;
  }

  private handleInput = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.valueInput.emit(this.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleChange = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.valueChange.emit(this.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleFocus = () => {
    this.clearFieldError.emit(this.getResolvedName());
  };

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = this.hostEl.parentElement;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }

  render() {
    // Generate id only if both label and name are present
    let textareaId: string | undefined = undefined;
    const resolvedName = this.getResolvedName();
    if (this.label && this.name) {
      textareaId = resolvedName;
    }
    const hasError = this.error === 'true';
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      if (this.errorType === 'required') errorText = this.requiredMessage || 'This field is required';
      else if (this.errorType === 'invalid') errorText = this.invalidMessage || 'Invalid value';
      else errorText = 'This field is invalid';
    }
    return (
      <div
        class={{
          'textarea': true,
          'textarea--disabled': this.disabled,
          'textarea--error': hasError,
        }}
      >
        {this.label && (
          <label htmlFor={textareaId} class="textarea__label">
            {this.label}
          </label>
        )}
        <textarea
          class={{
            'textarea__field': true,
            'textarea__field--error': hasError,
          }}
          {...(textareaId ? { id: textareaId } : {})}
          name={resolvedName}
          placeholder={this.placeholder}
          disabled={this.disabled}
          required={this.required}
          rows={this.rows}
          minLength={this.minLength}
          maxLength={this.maxLength}
          onInput={this.handleInput}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          aria-invalid={hasError ? 'true' : this.required && !this.value ? 'true' : 'false'}
          aria-required={this.required ? 'true' : 'false'}
          aria-disabled={this.disabled ? 'true' : 'false'}
        ></textarea>
        <me-text variant="small" class={`form-error-message ${hasError && errorText ? 'error-visible' : 'error-hidden'}`}>
          {errorText || '\u00A0'}
        </me-text>
      </div>
    );
  }
}
